import * as React from 'react';

import { Box, Flex } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { IconDelete } from '@lightspeed/flame/Icon/Delete';
import { Input } from '@lightspeed/flame/Input';
import { Spinner } from '@lightspeed/flame/Spinner';
import { Text } from '@lightspeed/flame/Text';

import { localizeDate } from 'root/apps/MakeTaxDigitalUK/utils';
import { useScanInput } from 'root/scan/useScanInput';

import { StyledButton, StyledRow } from './AddSerial.style';
import { useSerialNumberForm } from './useSerialNumberForm';

const DateTime = ({ timestamp }: { timestamp: string }) => {
  if (!timestamp) return null;

  const timestampParts = timestamp.split(' ');
  const monthDayYear = timestampParts[0];
  const time = `${timestampParts[1]} ${timestampParts[2]}`;

  return (
    <Flex flexDirection="row" flexWrap="wrap">
      <Text size="small" mr={1}>
        {monthDayYear}
      </Text>
      <Text size="small">{time}</Text>
    </Flex>
  );
};

export const SerialInput = ({
  rowNumber,
  isRowDisabled = false,
  isInputDisabled,
  isSaving,
  inputValue = '',
  date,
  dateFormat,
  index,
  serialNumberForm,
  errors,
  onChange,
  onBlur,
  onKeyDown,
  onDelete,
  showDeleteIcon = false,
}: {
  rowNumber: number;
  isRowDisabled?: boolean;
  isInputDisabled: boolean;
  isSaving?: boolean;
  inputValue: string;
  date?: string;
  dateFormat?: string;
  index: number;
  serialNumberForm?: ReturnType<typeof useSerialNumberForm>;
  errors: null | Record<string, any>;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onDelete?: () => void;
  showDeleteIcon?: boolean;
}) => {
  const { timeZone } = window.merchantos.session.getShop() || {};
  const { register, getValues } = serialNumberForm || {};

  const inputRef = useScanInput({ onKeyDown, onInput: onChange, onChange });

  return (
    <Box key={rowNumber} data-test="serial-input-row">
      <StyledRow alignItems="center" isRowDisabled={isRowDisabled}>
        <Box width="10%" pl={4} py={2} minWidth="60px">
          {rowNumber}
        </Box>
        <Box width="45%" px={4} py={2} flex="0 0 auto">
          <Flex flexDirection="column" width="100%">
            <Input
              ref={inputRef}
              readOnly={isInputDisabled || isRowDisabled}
              disabled={isInputDisabled || isRowDisabled}
              value={inputValue}
              autoFocus={rowNumber === 1 && !isInputDisabled}
              status={errors?.editableFields && errors.editableFields[index] ? 'error' : undefined}
              width="100%"
              {...(register && register(`editableFields[${index}].name` as const))}
              defaultValue={getValues && getValues(`editableFields[${index}].name`)}
              onChange={onChange}
              onBlur={onBlur}
              data-test="serial-input"
              className={errors?.editableFields?.[index] ? 'is-error' : ''}
              suffix={isSaving ? <Spinner color="gray-500" /> : null}
            />
            {errors?.editableFields?.[index] ? (
              <Text color="danger" fontSize="14px" mt={1}>
                {errors.editableFields[index].name.message}
              </Text>
            ) : null}
          </Flex>
        </Box>
        <Box width="30%" px={4} py={2}>
          {date && dateFormat && timeZone ? (
            <DateTime timestamp={localizeDate(date, dateFormat, timeZone)} />
          ) : (
            <Text> &#8211; </Text>
          )}
        </Box>
        {showDeleteIcon && onDelete ? (
          <Flex width="15%" px={4} py={2} justifyContent="flex-end">
            <StyledButton
              size="small"
              onClick={onDelete}
              disabled={isRowDisabled}
              data-test="delete-icon"
            >
              <IconDelete />
            </StyledButton>
          </Flex>
        ) : null}
      </StyledRow>
      <Divider />
    </Box>
  );
};
